import { Box, Button, Chip, Tooltip, Typography, useTheme } from "@mui/material";
import { OralModel } from "../../../../../models/Oral.model";
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { Favorite, Visibility } from "@mui/icons-material";
import { FC, useState } from 'react'
import NoteIcon from '@mui/icons-material/Note';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import styled from "styled-components";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CancelIcon from '@mui/icons-material/Cancel';
import IconButton from "@mui/material/IconButton";

interface OralItemProps {
    checked?: boolean,
    props: OralModel,
    onClick: (item: OralModel) => void,
    onSelect: (item: OralModel, checked: boolean) => void,
    onFavorite: (item: OralModel) => void,
    onDelete: (classification: string, props: OralModel) => void,
    annotationOpen?: (patient: OralModel) => void,
    classificationOpen: (patient: OralModel) => void,
    onPopUp: () => void
}

export const ClassificationChip = styled(Chip)`
    margin: 2px;
    min-width: 120px;
    max-width: 150px;
    display: flex;
    justify-content: center;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

export const OralItem: FC<OralItemProps> = ({props, onSelect, checked, onClick, onFavorite, annotationOpen, onPopUp, classificationOpen, onDelete }) => {
    const {name, classifications, userClassifications} = props
    const theme = useTheme()
    const [expanded, setExpanded] = useState(false)

    return (
        <Accordion
            expanded={expanded}
            onChange={() => {}}
            sx={{
                mb: "8px",
                borderRadius: "13px !important",
                border: `1.5px solid ${checked ? theme.palette.primary.main : theme.palette.divider}`,
                backgroundColor: checked ? theme.palette.brand.cream : "background.paper",
                boxShadow: "none",
                "&:before": { display: "none" },
                transition: "border-color 0.15s, background-color 0.15s",
            }}
        >
            <AccordionSummary
                onClick={() => onSelect(props, !checked)}
                sx={{ minHeight: 62, cursor: "pointer", "& .MuiAccordionSummary-content": { my: "12px" } }}
            >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Box
                            sx={{
                                width: 22,
                                height: 22,
                                borderRadius: "50%",
                                border: `2px solid ${checked ? theme.palette.primary.main : theme.palette.divider}`,
                                backgroundColor: checked ? theme.palette.primary.main : "transparent",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                transition: "background-color 0.15s, border-color 0.15s",
                            }}
                        >
                            {checked && (
                                <Box
                                    component="span"
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        backgroundColor: "primary.contrastText",
                                    }}
                                />
                            )}
                        </Box>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "text.primary" }}>
                            {name}
                        </Typography>
                    </Box>

                    <Box onClick={(e) => e.stopPropagation()} sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
                        <Tooltip title="Informações" placement="bottom">
                            <IconButton size="small" aria-label={`Informações de ${name}`} onClick={() => onClick(props)} sx={{ color: "primary.main" }}>
                                <Visibility sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Favoritar" placement="bottom">
                            <IconButton size="small" aria-label={`Favoritar ${name}`} onClick={() => onFavorite(props)}>
                                <Favorite color={props.favorite ? 'primary' : 'disabled'} sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Anotações" placement="bottom">
                            <IconButton
                                size="small"
                                aria-label={`Anotações de ${name}`}
                                onClick={() => (annotationOpen ? annotationOpen(props) : onPopUp())}
                                sx={{ color: annotationOpen ? "primary.main" : "text.disabled" }}
                            >
                                <NoteIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={expanded ? "Recolher" : "Ver classificações"} placement="bottom">
                            <IconButton
                                size="small"
                                aria-label={expanded ? "Recolher classificações" : "Ver classificações"}
                                aria-expanded={expanded}
                                onClick={() => setExpanded((v) => !v)}
                                sx={{ color: "text.secondary", transition: "transform 0.2s", transform: expanded ? "rotate(180deg)" : "none" }}
                            >
                                <ExpandMoreIcon sx={{ fontSize: 22 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ display: "flex", flexDirection: "row", alignItems: "center", pt: 0 }}>
                <Typography sx={{ fontSize: 12.5, color: "text.secondary", mr: "6px" }}>Classificações:</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                    {classifications?.map((classification, index) => (
                        <Chip
                            key={index}
                            label={classification}
                            color="primary"
                            size="small"
                            sx={{ margin: "2px" }}
                        />
                    ))}
                </Box>
            </AccordionDetails>

            <AccordionDetails sx={{ display: "flex", flexDirection: "row", alignItems: "center", pt: 0 }}>
                <Typography sx={{ fontSize: 12.5, color: "text.secondary", mr: "6px" }}>Classificações do usuário:</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                    <Button size="small" sx={{ minWidth: "30px", minHeight: "30px" }} onClick={() => classificationOpen(props)}>
                        <Tooltip title="Adicionar classificação" placement="bottom">
                            <AddCircleOutlineIcon sx={{ fontSize: 18, color: "primary.main" }}/>
                        </Tooltip>
                    </Button>
                    {userClassifications?.map((classification, index) => (
                        <ClassificationChip
                            key={index}
                            label={classification}
                            color="primary"
                            size="small"
                            deleteIcon={
                                <Tooltip title="Remover classificação">
                                    <IconButton size="small" sx={{ padding: 0 }}>
                                        <CancelIcon />
                                    </IconButton>
                                </Tooltip>
                            }
                            onDelete={() => onDelete(classification, props)}
                        />
                    ))}
                </Box>
            </AccordionDetails>
        </Accordion>
    )
}
